import { Component, computed, effect, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, signal, SimpleChanges, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { AudioRecorderComponent } from 'src/app/shared/components/recorders/audio-recorder/audio-recorder.component';
import { LiveDictationButtonComponent } from 'src/app/shared/components/recorders/live-dictation-button/live-dictation-button.component';
import { HtmlSplitterHelper } from 'src/app/shared/helpers/html-splitter.helper';
import { HtmlToMarkdownHelper } from 'src/app/shared/helpers/html-to-markdown.helper';
import { sanitizeQuillText } from 'src/app/shared/helpers/utils';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChunkService } from '../chunk/chunk.service';
import { ContentGeneratorService } from '../content-generator.service';
import { ContentStateService } from '../content-state.service';
import { InsertOptionsMenuComponent } from '../insert-options-menu/insert-options-menu.component';

@Component({
  selector: 'app-canvas-editor',
  templateUrl: './canvas-editor.component.html',
  styleUrls: ['./canvas-editor.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    QuillModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTooltipModule,
    AudioRecorderComponent,
    LiveDictationButtonComponent,
  ],
})
export class CanvasEditorComponent implements OnDestroy, OnChanges {
  @Input() contentId: string;
  @Input() messageId: string;
  @Input() title: string = '';
  @Input() content: string = '';
  @Input() source: 'conversation' | 'builder' | 'transcript' | 'import' = 'conversation';
  @Input() draftId: string;
  @Input() chunkId?: string;
  @Input() edition?: any;
  @Output() close = new EventEmitter<void>();

  public contentStateService = inject(ContentStateService);
  public sidenavService = inject(SidenavService);
  private contentGeneratorService = inject(ContentGeneratorService);
  private chunkService = inject(ChunkService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private confirmMenuService = inject(ConfirmMenuService);
  private dynamicOverlayService = inject(DynamicOverlayService);
  private translate = inject(TranslateService);

  @ViewChild('editor') editor: QuillEditorComponent;
  @ViewChild('canvasContent') canvasContent!: ElementRef<HTMLDivElement>;

  canUndo = signal(false);
  canRedo = signal(false);
  currentHeaderLabel = signal('Quill.Header.Normal');
  saveStatus = signal<'saving' | 'saved' | ''>('');
  isInserting = signal(false);
  editableContent = '';
  isWriter = computed(() => this.authService.isWriter() && !this.contentStateService.isReader());
  isGeneratingAudio = computed(() => {
    const playerState = this.contentStateService.playerState();
    const isBuffering = playerState?.chunkId === this.chunkId && playerState.status === 'buffering';
    const tasks = this.contentStateService.activeTasks();
    const isRemoteGenerating = this.chunkId ? tasks[this.chunkId]?.audio?.status === 'generating' : false;
    return isBuffering || isRemoteGenerating;
  });
  isPlaying = computed(() => {
    const playerState = this.contentStateService.playerState();
    return playerState?.chunkId === this.chunkId && playerState.status === 'playing';
  });

  private quillWordRanges: { textNode: Node; startOffset: number; endOffset: number; word: string }[] = [];
  private isQuillTokenized = false;
  private lastChunkId = '';
  private lastLanguage = '';

  private autoSaveSubject = new Subject<string>();
  private autoSaveSubscription: Subscription;
  private liveTranscriptionSub: Subscription | undefined;
  private liveTranscriptionStartedSub: Subscription | undefined;
  private initialContentBeforeDictation = '';
  dictationInsertedLength: number = 0;

  headerOptions = [
    { id: 1, text: 'Quill.Header.1', icon: 'format_h1' },
    { id: 2, text: 'Quill.Header.2', icon: 'format_h2' },
    { id: 3, text: 'Quill.Header.3', icon: 'format_h3' },
    { id: 4, text: 'Quill.Header.4', icon: 'format_h4' },
    { id: 5, text: 'Quill.Header.5', icon: 'format_h5' },
    { id: 6, text: 'Quill.Header.6', icon: 'format_h6' },
    { id: false, text: 'Quill.Header.Normal', icon: 'text_fields' },
  ];

  quillModules = {
    toolbar: '#header-toolbar',
    keyboard: {
      bindings: {
        tab: {
          key: 9,
          handler: () => true, // Default handling
        },
      },
    },
  };

  // TODO: [RTA-1917] devrait normalement transformer les backtick en code mais ne fonctionne pas pour le moment
  // à revoir.
  onEditorCreated(quill: any) {
    quill.on('text-change', (delta: any, oldDelta: any, source: string) => {
      this.isQuillTokenized = false;
      if (source !== 'user') return;
      const ops = delta.ops;
      if (!ops || ops.length === 0) return;

      const lastOp = ops[ops.length - 1];
      if (lastOp && lastOp.insert === '`') {
        const selection = quill.getSelection();
        if (!selection) return;

        const index = selection.index;
        const text = quill.getText(0, index - 1);
        const lastLineBreak = text.lastIndexOf('\n');
        const searchStart = lastLineBreak !== -1 ? lastLineBreak + 1 : 0;
        const lineText = text.substring(searchStart);

        const lastBacktickIndexInLine = lineText.lastIndexOf('`');
        if (lastBacktickIndexInLine !== -1) {
          const startPos = searchStart + lastBacktickIndexInLine;
          const codeText = text.substring(startPos + 1);

          if (codeText.length > 0) {
            quill.deleteText(index - 1, 1);
            quill.deleteText(startPos, 1);

            quill.formatText(startPos, codeText.length, 'code', true);
            quill.setSelection(startPos + codeText.length);
            quill.format('code', false);
          }
        }
      }
    });
  }

  constructor() {
    this.autoSaveSubscription = this.autoSaveSubject.pipe(debounceTime(2000)).subscribe(() => {
      this.performSave();
    });

    this.translate.onLangChange.subscribe(() => {
      this.isQuillTokenized = false;
      this.clearQuillHighlighting();
    });

    effect(() => {
      const playing = this.isPlaying();
      const currentTime = this.contentStateService.playerCurrentTime();
      const chunkId = this.chunkId;
      const currentLang = this.translate.currentLang;

      if (chunkId !== this.lastChunkId || currentLang !== this.lastLanguage) {
        this.clearQuillHighlighting();
        this.lastChunkId = chunkId || '';
        this.lastLanguage = currentLang || '';
        this.isQuillTokenized = false;
      }

      if (playing && chunkId) {
        this.highlightQuillWordAtTime(currentTime);
      } else {
        this.clearQuillHighlighting();
      }
    });
  }

  ngOnInit() {
    this.liveTranscriptionSub = this.chunkService.liveTranscriptionData$.subscribe((data) => {
      if (data && data.text) {
        if (this.contentStateService.liveTranscriptionSource() === 'dictation') {
          if (this.editor?.quillEditor) {
            const quill = this.editor.quillEditor;
            const docLength = quill.getLength();
            const Delta = (quill.constructor as any).import('delta');

            const dictationDelta = quill.clipboard.convert({ html: data.text });
            
            if (dictationDelta.ops.length > 0) {
              const lastOp = dictationDelta.ops[dictationDelta.ops.length - 1];
              if (typeof lastOp.insert === 'string' && lastOp.insert.endsWith('\n')) {
                lastOp.insert = lastOp.insert.slice(0, -1);
                if (lastOp.insert.length === 0) {
                  dictationDelta.ops.pop();
                }
              }
            }

            const insertIndex = Math.max(0, docLength - 1 - this.dictationInsertedLength);

            const charBeforeInsert = insertIndex > 0 ? quill.getText(insertIndex - 1, 1) : '\n';
            const needsSpace = charBeforeInsert !== '\n' && charBeforeInsert !== ' ';

            if (needsSpace) {
              if (dictationDelta.ops.length > 0 && typeof dictationDelta.ops[0].insert === 'string') {
                const firstStr = dictationDelta.ops[0].insert;
                if (!firstStr.startsWith(' ') && !firstStr.startsWith('\n')) {
                  dictationDelta.ops[0].insert = ' ' + firstStr;
                }
              }
            }
            
            const updateDelta = new Delta()
              .retain(insertIndex)
              .delete(this.dictationInsertedLength)
              .concat(dictationDelta);

            quill.updateContents(updateDelta, 'silent');
            
            this.dictationInsertedLength = dictationDelta.length();
            quill.setSelection(quill.getLength(), 0, 'silent');
            this.editableContent = quill.root.innerHTML;

            if (this.canvasContent?.nativeElement) {
              const container = this.canvasContent.nativeElement;
              setTimeout(() => {
                container.scrollTop = container.scrollHeight;
              }, 50);
            }

            if (data.isFinal) {
              this.saveStatus.set('saving');
              this.autoSaveSubject.next(this.editableContent);
              this.updateHistoryState();
            }
          }
        } else {
          // audio-recorder provides the FULL transcribed text (with interim in italics if available)
          this.editableContent = data.text;

          if (this.editor?.quillEditor) {
            const quill = this.editor.quillEditor;
            const range = quill.getSelection();
            const oldLength = quill.getLength();

            const delta = quill.clipboard.convert({ html: this.editableContent });
            quill.setContents(delta, 'silent');

            const newLength = quill.getLength();

            if (range) {
              if (range.index >= oldLength - 1) {
                quill.setSelection(newLength, 0, 'silent');
              } else {
                quill.setSelection(range.index, range.length, 'silent');
              }
            } else {
              quill.setSelection(newLength, 0, 'silent');
            }

            if (this.canvasContent?.nativeElement) {
              const container = this.canvasContent.nativeElement;
              setTimeout(() => {
                container.scrollTop = container.scrollHeight;
              }, 50);
            }

            if (data.isFinal) {
              this.saveStatus.set('saving');
              this.autoSaveSubject.next(this.editableContent);
              this.updateHistoryState();
            }
          }
        }
      }
    });

    this.liveTranscriptionStartedSub = this.chunkService.liveTranscriptionStarted$.subscribe((started) => {
      if (started) {
        this.dictationInsertedLength = 0;
        this.initialContentBeforeDictation = this.editableContent;
      } else {
        this.initialContentBeforeDictation = '';
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['content']) {
      this.editableContent = this.content || '';
      if (this.editor?.quillEditor) {
        const delta = this.editor.quillEditor.clipboard.convert({ html: this.editableContent });
        this.editor.quillEditor.setContents(delta, 'silent');
      }
    }
  }

  ngOnDestroy() {
    if (this.saveStatus() === 'saving') {
      this.performSave();
    }
    this.autoSaveSubscription?.unsubscribe();
    this.liveTranscriptionSub?.unsubscribe();
    this.liveTranscriptionStartedSub?.unsubscribe();
    if (this.source === 'transcript') {
      this.contentStateService.closeContentGenerator();
    }
    if (this.isPlaying()) {
      this.contentStateService.clearAudio();
    }
  }

  onClose() {
    if (this.saveStatus() === 'saving') {
      this.performSave();
    }
    this.close.emit();
  }

  onContentChanged(event: any) {
    if (!this.isWriter()) return;
    // Only trigger autosave if the change is from the user
    // or if it's a manual trigger we sent as { source: 'user' }
    if (event.source === 'user') {
      if (event.html !== undefined) {
        this.editableContent = event.html;
      }
      this.saveStatus.set('saving');
      this.autoSaveSubject.next(this.editableContent);
      this.updateHistoryState();
    }
  }

  saveEdit() {
    if (!this.isWriter()) return;
    this.performSave(true);
  }

  togglePlayback() {
    if (!this.chunkId) return;

    if (this.isPlaying()) {
      this.contentStateService.pauseAudio();
      return;
    }

    if (this.isWriter()) {
      const sanitizedContent = sanitizeQuillText(this.editableContent);
      const isContentChanged = this.content !== sanitizedContent;

      if (isContentChanged) {
        this.content = sanitizedContent;
        const editData = {
          id: this.chunkId,
          edition: this.edition,
          content: this.content,
          contentID: this.contentId,
          language: this.translate.currentLang,
        };

        this.saveStatus.set('saving');
        this.chunkService.editChunk(editData).subscribe({
          next: (res) => {
            this.updateSaveStatus();
            const chunkToPlay = res?.chunk || this.contentStateService.content().find((c) => c._id === this.chunkId);
            if (chunkToPlay) {
              this.playAndTrackGeneration(chunkToPlay);
            }
          },
          error: (err) => {
            console.error('Failed to save chunk before playback', err);
            this.saveStatus.set('');
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
          },
        });
      } else {
        const chunkToPlay = this.contentStateService.content().find((c) => c._id === this.chunkId);
        if (chunkToPlay) {
          this.playAndTrackGeneration(chunkToPlay);
        }
      }
    } else {
      const chunkToPlay = this.contentStateService.content().find((c) => c._id === this.chunkId);
      if (chunkToPlay) {
        this.playAndTrackGeneration(chunkToPlay);
      }
    }
  }

  private playAndTrackGeneration(chunk: any) {
    this.contentStateService.playTrack(this.chunkId, chunk, true);
  }

  private tokenizeQuillContent() {
    if (this.isQuillTokenized) return;
    const quill = this.editor?.quillEditor;
    if (!quill) return;
    const root = quill.root;
    if (!root) return;

    this.quillWordRanges = [];
    const traverse = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const parent = node.parentNode;
        if (!parent) return;

        const parentTagName = (parent as HTMLElement).tagName?.toLowerCase();
        if (['script', 'style', 'code', 'noscript'].includes(parentTagName)) {
          return;
        }
        if ((parent as HTMLElement).closest('.ql-formula') || (parent as HTMLElement).closest('.katex')) {
          return;
        }

        const text = node.nodeValue || '';
        if (!text.trim()) return;

        const regex = /([a-zA-Z0-9À-ÿ\u00C0-\u017F'-]+)/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
          const matchIndex = match.index;
          const matchedWord = match[0];
          this.quillWordRanges.push({
            textNode: node,
            startOffset: matchIndex,
            endOffset: matchIndex + matchedWord.length,
            word: matchedWord
          });
        }
      } else {
        const children = Array.from(node.childNodes);
        for (const child of children) {
          traverse(child);
        }
      }
    };

    traverse(root);
    this.isQuillTokenized = true;
  }

  private highlightQuillWordAtTime(currentTime: number) {
    if (!this.chunkId) return;

    const chunkObj = this.contentStateService.content().find((c) => c._id === this.chunkId);
    const currentLang = this.translate.currentLang;
    const currentAudio = chunkObj?.audios?.find(
      (a: any) =>
        a._id === currentLang ||
        a.id === currentLang ||
        a.filename?.includes(`_${currentLang}.`) ||
        a.fileLink?.includes(`_${currentLang}.`)
    );
    const words = currentAudio?.words || [];

    if (!words || words.length === 0) {
      this.clearQuillHighlighting();
      return;
    }

    let activeIndex = -1;
    const padding = 0.1; // 100ms padding to account for lag
    for (let i = words.length - 1; i >= 0; i--) {
      if (words[i].startTime <= currentTime + padding) {
        activeIndex = i;
        break;
      }
    }

    this.tokenizeQuillContent();

    if (this.quillWordRanges.length === 0) {
      this.clearQuillHighlighting();
      return;
    }

    if (activeIndex >= this.quillWordRanges.length) {
      activeIndex = this.quillWordRanges.length - 1;
    }

    if (activeIndex === -1) {
      this.clearQuillHighlighting();
      return;
    }

    const wordRange = this.quillWordRanges[activeIndex];
    try {
      const range = new Range();
      range.setStart(wordRange.textNode, wordRange.startOffset);
      range.setEnd(wordRange.textNode, wordRange.endOffset);

      const win = window as any;
      if (typeof win.CSS !== 'undefined' && win.CSS.highlights) {
        if (win.Highlight) {
          const highlight = new win.Highlight(range);
          win.CSS.highlights.set('quill-word-highlight', highlight);
        }
      }
    } catch (e) {
      console.error('Failed to highlight word in Quill editor:', e);
    }
  }

  private clearQuillHighlighting() {
    const win = window as any;
    if (typeof win.CSS !== 'undefined' && win.CSS.highlights) {
      win.CSS.highlights.delete('quill-word-highlight');
    }
  }

  toggleContentGenerator() {
    if (this.source === 'transcript' && this.chunkId) {
      if (this.contentStateService.isGeneratorOpen()) {
        this.contentStateService.closeContentGenerator();
      } else {
        this.contentStateService.openContentGenerator({
          initialSubject: this.translate.instant('Global.Transcript'),
          context: {
            id: this.chunkId,
            title: this.translate.instant('Global.Transcript'),
            name: this.translate.instant('Global.Transcript'),
            type: 'chunk',
          },
        });
      }
    }
  }

  private performSave(isManual: boolean = false) {
    if (!this.isWriter()) return;
    this.content = sanitizeQuillText(this.editableContent);

    if (this.source === 'conversation') {
      const fullContent = { title: this.title, html: this.content };
      this.contentStateService.updateCanvasContent(this.contentId, this.messageId, fullContent).subscribe({
        next: () => {
          this.updateSaveStatus();
          if (isManual) {
            this.showSaveFeedback(true);
          }
        },
        error: (err) => {
          console.error('Save failed', err);
          if (isManual) {
            this.showSaveFeedback(false);
          }
        },
      });
    } else if (this.source === 'builder' && this.draftId) {
      this.contentGeneratorService.updateDraft(this.draftId, { content: this.content }).subscribe({
        next: () => {
          this.updateSaveStatus();
          if (isManual) {
            this.showSaveFeedback(true);
          }
        },
        error: (err) => {
          console.error('Save failed', err);
          if (isManual) {
            this.showSaveFeedback(false);
          }
        },
      });
    } else if (this.source === 'transcript' && this.chunkId && this.edition) {
      const editData = {
        id: this.chunkId,
        edition: this.edition,
        content: this.content,
        contentID: this.contentId,
        language: this.translate.currentLang,
      };
      this.chunkService.editChunk(editData).subscribe({
        next: () => {
          this.updateSaveStatus();
          if (isManual) {
            this.showSaveFeedback(true);
          }
        },
        error: (err) => {
          console.error('Save failed', err);
          if (isManual) {
            this.showSaveFeedback(false);
          }
        },
      });
    } else if (this.source === 'import') {
      this.updateSaveStatus();
      if (isManual) {
        this.showSaveFeedback(true);
      }
    }
  }

  private showSaveFeedback(success: boolean) {
    const translationKey = success ? 'Snackbar.SavedMessage' : 'Snackbar.Error';
    this.toast.snackbar.open(this.translate.instant(translationKey), 'Ok', { duration: 3000 });
  }

  private updateSaveStatus() {
    this.saveStatus.set('saved');

    // Clear the "Saved" message after a delay
    setTimeout(() => {
      if (this.saveStatus() === 'saved') {
        this.saveStatus.set('');
      }
    }, 1000);
  }

  undo() {
    if (!this.isWriter()) return;
    this.editor?.quillEditor?.history?.undo();
    this.updateHistoryState();
    // Manual trigger since history undo might have source 'api' in some contexts
    this.onContentChanged({ source: 'user' });
  }

  redo() {
    if (!this.isWriter()) return;
    this.editor?.quillEditor?.history?.redo();
    this.updateHistoryState();
    // Manual trigger
    this.onContentChanged({ source: 'user' });
  }

  applyHeader(level: number | boolean) {
    if (!this.isWriter()) return;
    this.editor.quillEditor.format('header', level, 'user');
    this.updateHeaderLabel(level);
  }

  clearFormatting() {
    if (!this.isWriter()) return;
    const range = this.editor.quillEditor.getSelection();
    if (range) {
      this.editor.quillEditor.removeFormat(range.index, range.length, 'user');
      this.updateHeaderLabel(false); // Reset to Normal
    }
  }

  onSelectionChanged(event: any) {
    if (event.range) {
      const format = this.editor.quillEditor.getFormat(event.range.index, event.range.length);
      const headerLevel = format.header || false;
      this.updateHeaderLabel(headerLevel);
      this.updateHistoryState();
    }
  }

  private updateHeaderLabel(level: any) {
    const found = this.headerOptions.find((opt) => opt.id === level);
    if (found) {
      this.currentHeaderLabel.set(found.text);
    } else {
      this.currentHeaderLabel.set('Quill.Header.Normal');
    }
  }

  updateHistoryState() {
    if (this.editor?.quillEditor?.history) {
      const stack = this.editor.quillEditor.history.stack;
      this.canUndo.set(stack.undo.length > 0);
      this.canRedo.set(stack.redo.length > 0);
    }
  }

  confirmInsert(event: Event) {
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement;

    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(trigger, InsertOptionsMenuComponent);

    const sub = componentInstance.optionSelected.subscribe((option: 'chunks' | 'canvas' | 'markdown') => {
      this.dynamicOverlayService.close();
      this.insertContent(option);
    });

    overlayRef.detachments().subscribe(() => {
      sub.unsubscribe();
    });
  }

  async insertContent(insertMode: 'chunks' | 'canvas' | 'markdown' = 'chunks') {
    if (!this.isWriter() || this.isInserting()) return;
    this.isInserting.set(true);

    const content = this.editableContent;

    if (!content.trim()) {
      this.toast.snackbar.open('No content to insert', 'Ok', { duration: 2000 });
      this.isInserting.set(false);
      return;
    }

    try {
      const userData = this.authService.userInfo();
      const safeUser = userData || JSON.parse(localStorage.getItem('userData') || '{}');

      let chunkPayloads: any[];

      if (insertMode === 'chunks') {
        const chunks = HtmlSplitterHelper.split(content);
        if (chunks.length === 0) {
          this.toast.snackbar.open('No content to insert', 'Ok', { duration: 2000 });
          this.isInserting.set(false);
          return;
        }

        const parser = new DOMParser();
        chunkPayloads = chunks.map((chunkHtml) => {
          const doc = parser.parseFromString(chunkHtml, 'text/html');
          const element = doc.body.firstElementChild;

          let isCodeBlock = false;
          let codeContent = '';
          let codeLanguage = '';

          if (element && element.tagName === 'PRE') {
            isCodeBlock = true;
            codeContent = element.textContent || '';

            const codeElem = element.querySelector('code');
            const classes: string[] = [];

            if (element.className) {
              classes.push(...element.className.split(/\s+/));
            }
            if (codeElem && codeElem.className) {
              classes.push(...codeElem.className.split(/\s+/));
            }

            for (const cls of classes) {
              const match = cls.match(/^(?:language|lang)-(\w+)$/);
              if (match) {
                codeLanguage = match[1];
                break;
              }
            }

            const dataLang = element.getAttribute('data-language') || (codeElem && codeElem.getAttribute('data-language'));
            if (dataLang) {
              codeLanguage = dataLang;
            }

            if (!codeLanguage) {
              codeLanguage = 'javascript';
            }
          }

          return {
            _id: Date.now().toString() + Math.random().toString().slice(2, 8),
            content: isCodeBlock ? codeContent : chunkHtml,
            type: isCodeBlock ? 'code' : 'html',
            ...(isCodeBlock && { codeLanguage }),
            language: this.translate.currentLang,
            status: 'creating',
            params: {},
            editions: [],
            user: {
              id: safeUser?.id,
              fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
              firstName: safeUser?.firstName,
              lastName: safeUser?.lastName,
              email: safeUser?.email,
            },
            courseID: this.contentStateService.courseId,
            lecture: this.contentStateService.lecture,
            section: this.contentStateService.section,
            blogID: this.contentStateService.blogId,
            noteID: this.contentStateService.noteId,
            contentID: this.contentStateService.contentId,
            contentName: this.title || 'Generated Content',
          };
        });
      } else if (insertMode === 'markdown') {
        chunkPayloads = [
          {
            _id: Date.now().toString() + Math.random().toString().slice(2, 8),
            content: HtmlToMarkdownHelper.convert(content),
            type: 'code',
            codeLanguage: 'markdown',
            language: this.translate.currentLang,
            status: 'creating',
            params: {},
            editions: [],
            user: {
              id: safeUser?.id,
              fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
              firstName: safeUser?.firstName,
              lastName: safeUser?.lastName,
              email: safeUser?.email,
            },
            courseID: this.contentStateService.courseId,
            lecture: this.contentStateService.lecture,
            section: this.contentStateService.section,
            blogID: this.contentStateService.blogId,
            noteID: this.contentStateService.noteId,
            contentID: this.contentStateService.contentId,
            title: this.title || 'Markdown Content',
            contentName: this.title || 'Generated Content',
          },
        ];
      } else {
        chunkPayloads = [
          {
            _id: Date.now().toString() + Math.random().toString().slice(2, 8),
            content: content,
            type: 'canvas-transcript',
            language: this.translate.currentLang,
            status: 'creating',
            params: {},
            editions: [],
            user: {
              id: safeUser?.id,
              fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
              firstName: safeUser?.firstName,
              lastName: safeUser?.lastName,
              email: safeUser?.email,
            },
            courseID: this.contentStateService.courseId,
            lecture: this.contentStateService.lecture,
            section: this.contentStateService.section,
            blogID: this.contentStateService.blogId,
            noteID: this.contentStateService.noteId,
            contentID: this.contentStateService.contentId,
            title: this.title || 'Transcription',
            contentName: this.title || 'Generated Content',
          },
        ];
      }

      await firstValueFrom(
        this.chunkService.createChunk({
          chunks: chunkPayloads,
          insertBelowID: undefined,
        })
      );

      this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Ok', { duration: 2000 });
      this.close.emit();
    } catch (e) {
      console.error('Failed to insert content', e);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
    } finally {
      this.isInserting.set(false);
    }
  }
}
